import {
  useRef, useState, useMemo, useCallback, useEffect,
} from 'react';
import { FlatList, Dimensions, LayoutChangeEvent } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  useSharedValue,
  useAnimatedScrollHandler,
} from 'react-native-reanimated';
import { useAuthStore } from '../../../common/auth/store';
import { useActivity } from '../../../common/hooks/student/useActivity';
import { useModuleAndTaskSelectionState } from '../../../common/context/AppStateStore';
import { useCourseData } from '../../../common/hooks/student/useCourseData';
import { useSelectedModule } from '../../../common/hooks/student/useSelectedModule';
import { getModuleState } from '../../../common/utils/moduleState';
import { useDialog } from '../../../common/context/DialogContext';
import { Module, TimelineItem } from '../types';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

/**
 * Custom hook that encapsulates the business logic for the CourseScreen.
 * Handles data fetching, state management, and scroll synchronization.
 */
export const useCourseScreen = () => {
  const { t } = useTranslation();
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const { logSelectModule } = useActivity();
  const { selectedModule, setSelectedModule } = useModuleAndTaskSelectionState();
  const {
    modules, isLoading, error, refetchCourseData,
  } = useCourseData();
  const { module: currentModule } = useSelectedModule();
  const { showToastMessage, showDialog } = useDialog();

  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [pageHeight, setPageHeight] = useState<number>(SCREEN_HEIGHT);
  const pageHeightSV = useSharedValue<number>(SCREEN_HEIGHT);

  const scrollY = useSharedValue<number>(0);
  const flatListRef = useRef<FlatList>(null);
  const timelineRef = useRef<FlatList>(null);

  useEffect(() => {
    if (error) {
      showToastMessage('error', t('student.course.errors.server-error'));
    }
  }, [error, showToastMessage, t]);

  const handleLayout = useCallback((event: LayoutChangeEvent): void => {
    const { height } = event.nativeEvent.layout;
    if (height > 0) {
      // Use 92% of height to create a 'peek' effect for the next module
      const adjustedHeight = height * 0.92;
      if (Math.abs(adjustedHeight - pageHeight) > 1) {
        setPageHeight(adjustedHeight);
        pageHeightSV.value = adjustedHeight;
      }
    }
  }, [pageHeight, pageHeightSV]);

  const sortedModules = useMemo((): Module[] => {
    if (!modules) return [];

    // Filter out invalid modules and deduplicate by ID
    const seenIds = new Set<string>();
    const validModules = modules.filter((m): m is Module => {
      if (!(m && m.id && m.state)) return false;
      if (seenIds.has(m.id)) return false;
      seenIds.add(m.id);
      return true;
    });

    // 1. Sort modules by segment and then by sortIndex within segment
    const primaryCourse = validModules[0]?.courses?.[0];
    const segments = primaryCourse?.segments || [];
    const sortedSegments = [...segments].sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));

    const baseModules = [...validModules].sort((a, b) => {
      const segmentA = a.courseSettings?.segmentId;
      const segmentB = b.courseSettings?.segmentId;

      if (segmentA !== segmentB) {
        const indexA = sortedSegments.findIndex((s) => s.id === segmentA);
        const indexB = sortedSegments.findIndex((s) => s.id === segmentB);
        // Treat unassigned segments as being at the end or start depending on requirements
        // Here we put unassigned at the end
        const finalIndexA = indexA === -1 ? 999 : indexA;
        const finalIndexB = indexB === -1 ? 999 : indexB;
        if (finalIndexA !== finalIndexB) return finalIndexA - finalIndexB;
      }

      const sortA = a.courseSettings?.sortIndex || 0;
      const sortB = b.courseSettings?.sortIndex || 0;
      return sortA - sortB;
    });

    return baseModules.map((m, index) => ({
      ...m,
      uiState: getModuleState(m, baseModules),
      originalIndex: index + 1,
    }));
  }, [modules]);

  const timelineItems = useMemo((): TimelineItem[] => {
    const items: TimelineItem[] = [
      {
        type: 'dashboard',
        id: 'dashboard',
        originalIndex: 0,
      },
    ];

    sortedModules.forEach((module) => {
      items.push({
        type: 'module',
        ...module,
      });
    });
    return items;
  }, [sortedModules]);

  const mainListData = useMemo(() => [
    { type: 'dashboard' as const, id: 'dashboard' },
    ...sortedModules.map((m) => ({ type: 'module' as const, module: m, id: m.id })),
  ], [sortedModules]);

  const onScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y;
    },
  });

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetchCourseData().then(() => setRefreshing(false));
  }, [refetchCourseData]);

  const handleTimelinePress = useCallback((index: number): void => {
    flatListRef.current?.scrollToIndex({ index, animated: true });
  }, []);

  const handleScrollDown = useCallback((): void => {
    const height = pageHeightSV.value;
    const nextIndex = Math.floor(scrollY.value / height) + 1;
    if (nextIndex < mainListData.length) {
      flatListRef.current?.scrollToIndex({ index: nextIndex, animated: true });
    }
  }, [mainListData.length, pageHeightSV, scrollY]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncTimelineScroll = useCallback(
    (itemIndex: number) => {
      if (itemIndex >= 0 && itemIndex < timelineItems.length) {
        timelineRef.current?.scrollToIndex({
          index: itemIndex,
          animated: true,
          viewPosition: 0.5,
        });
      }
    },
    [timelineItems.length],
  );

  return {
    user,
    logout,
    isLoading,
    refreshing,
    pageHeight,
    pageHeightSV,
    scrollY,
    sortedModules,
    timelineItems,
    mainListData,
    flatListRef,
    timelineRef,
    selectedModule,
    currentModule,
    onScroll,
    onRefresh,
    handleLayout,
    handleTimelinePress,
    handleScrollDown,
    syncTimelineScroll,
    setSelectedModule,
    logSelectModule,
    showToastMessage,
    showDialog,
    t,
  };
};
